import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDataProvider, useGetList, useNotify } from "ra-core";
import { ChevronDown, ChevronUp, Mail, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import {
  draftStepError,
  moveDraftStep,
  planStepWrites,
  type DraftStep,
} from "../../../../supabase/functions/_shared/sequenceEditor.ts";
import { NEW_LEAD_SEQUENCE_NAME } from "../../../../supabase/functions/_shared/sequencePlan.ts";
import { STUDIO_FROM } from "../../../../supabase/functions/_shared/vogelkop.ts";
import type { FollowUpSequence, FollowUpStep } from "../types";

const newKey = () =>
  globalThis.crypto?.randomUUID?.() ?? `step-${Date.now()}-${Math.random()}`;

const delayLabel = (days: number) => {
  if (days === 0) return "Sends when the lead is enrolled.";
  if (days === 1) return "Sends 1 day after enrollment.";
  return `Sends ${days} days after enrollment.`;
};

export const SequencesPage = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { data: sequences = [], isPending: sequencesPending } =
    useGetList<FollowUpSequence>("follow_up_sequences", {
      pagination: { page: 1, perPage: 20 },
      sort: { field: "id", order: "ASC" },
    });
  const {
    data: steps = [],
    isPending: stepsPending,
    refetch,
  } = useGetList<FollowUpStep>("follow_up_steps", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "position", order: "ASC" },
  });
  const sequence =
    sequences.find((item) => item.name === NEW_LEAD_SEQUENCE_NAME) ??
    sequences[0];
  const savedSteps = steps
    .filter((step) => !sequence || step.sequence_id === sequence.id)
    .sort((a, b) => a.position - b.position || a.day_offset - b.day_offset);

  const [name, setName] = useState(NEW_LEAD_SEQUENCE_NAME);
  const [draft, setDraft] = useState<DraftStep[]>([]);
  const [applied, setApplied] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savedKey =
    sequencesPending || stepsPending
      ? ""
      : `${sequence?.id ?? "new"}:${savedSteps
          .map((step) => `${step.id}:${step.position}`)
          .join(",")}`;

  useEffect(() => {
    if (!savedKey || applied === savedKey) return;
    setName(sequence?.name ?? NEW_LEAD_SEQUENCE_NAME);
    setDraft(
      savedSteps.map((step) => ({
        key: String(step.id),
        id: step.id,
        dayOffset: step.day_offset,
        subject: step.subject ?? "",
        body: step.body,
      })),
    );
    setApplied(savedKey);
  }, [applied, savedKey, savedSteps, sequence]);

  const updateStep = (key: string, patch: Partial<DraftStep>) => {
    setDraft((current) =>
      current.map((step) => (step.key === key ? { ...step, ...patch } : step)),
    );
  };

  const addStep = () => {
    const last = draft[draft.length - 1];
    setDraft((current) => [
      ...current,
      {
        key: newKey(),
        dayOffset: last ? last.dayOffset + 1 : 0,
        subject: "",
        body: "",
      },
    ]);
  };

  const save = async () => {
    const problem = draft.map(draftStepError).find(Boolean);
    if (!name.trim()) {
      setError("Name the sequence.");
      return;
    }
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let sequenceId = sequence?.id;
      if (sequenceId == null) {
        const created = await dataProvider.create<FollowUpSequence>(
          "follow_up_sequences",
          { data: { name: name.trim() } },
        );
        sequenceId = created.data.id;
      } else if (name.trim() !== sequence?.name) {
        await dataProvider.update("follow_up_sequences", {
          id: sequenceId,
          data: { name: name.trim() },
          previousData: sequence,
        });
      }
      const plan = planStepWrites(savedSteps, draft);
      for (const id of plan.removeIds) {
        const previous = savedSteps.find((step) => step.id === id);
        await dataProvider.delete("follow_up_steps", {
          id,
          previousData: previous,
        });
      }
      const nextDraft: DraftStep[] = [];
      for (const [position, step] of draft.entries()) {
        const subject = step.subject.trim() ? step.subject.trim() : null;
        const body = step.body.trim();
        if (step.id == null) {
          const created = await dataProvider.create<FollowUpStep>(
            "follow_up_steps",
            {
              data: {
                sequence_id: sequenceId,
                day_offset: step.dayOffset,
                subject,
                body,
                position,
              },
            },
          );
          nextDraft.push({
            key: String(created.data.id),
            id: created.data.id,
            dayOffset: step.dayOffset,
            subject: step.subject.trim(),
            body,
          });
        } else {
          const previous = savedSteps.find((item) => item.id === step.id);
          await dataProvider.update("follow_up_steps", {
            id: step.id,
            data: {
              day_offset: step.dayOffset,
              subject,
              body,
              position,
            },
            previousData: previous,
          });
          nextDraft.push({
            key: String(step.id),
            id: step.id,
            dayOffset: step.dayOffset,
            subject: step.subject.trim(),
            body,
          });
        }
      }
      setDraft(nextDraft);
      await queryClient.invalidateQueries({ queryKey: ["follow_up_steps"] });
      await queryClient.invalidateQueries({
        queryKey: ["follow_up_sequences"],
      });
      notify("Sequence saved");
      await refetch();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl pb-16">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Sequences</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            New leads are enrolled in this email sequence. It stops when the
            lead replies or the stage changes. Mail is sent from {STUDIO_FROM}.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => void save()}
          disabled={busy || !applied}
        >
          Save
        </Button>
      </div>

      <section aria-label="Sequence editor" className="space-y-4">
        <label className="block space-y-1">
          <span className="text-sm font-medium">Sequence</span>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            aria-label="Sequence name"
          />
        </label>
        <p className="text-sm text-muted-foreground">
          Saved steps are used the next time a new lead is enrolled.
        </p>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <ol className="space-y-4">
          {draft.map((step, index) => (
            <li
              key={step.key}
              className="space-y-3 rounded-xl border bg-card p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1 text-sm font-medium">
                    <Mail className="size-4" />
                    Email {index + 1}
                  </span>
                  <label className="flex items-center gap-2 text-sm">
                    Delay
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      className="w-20"
                      value={step.dayOffset}
                      aria-label={`Delay for email ${index + 1}`}
                      onChange={(event) =>
                        updateStep(step.key, {
                          dayOffset: Number(event.target.value),
                        })
                      }
                    />
                    days
                  </label>
                </div>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={`Move email ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() =>
                      setDraft((current) => moveDraftStep(current, index, -1))
                    }
                  >
                    <ChevronUp />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={`Move email ${index + 1} down`}
                    disabled={index === draft.length - 1}
                    onClick={() =>
                      setDraft((current) => moveDraftStep(current, index, 1))
                    }
                  >
                    <ChevronDown />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={`Delete email ${index + 1}`}
                    onClick={() =>
                      setDraft((current) =>
                        current.filter((item) => item.key !== step.key),
                      )
                    }
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {delayLabel(step.dayOffset)}
              </p>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Subject</span>
                <Input
                  value={step.subject}
                  aria-label={`Subject for email ${index + 1}`}
                  onChange={(event) =>
                    updateStep(step.key, { subject: event.target.value })
                  }
                />
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Body</span>
                <Textarea
                  value={step.body}
                  aria-label={`Body for email ${index + 1}`}
                  onChange={(event) =>
                    updateStep(step.key, { body: event.target.value })
                  }
                />
              </label>
              <p className="text-xs text-muted-foreground">
                {"{first_name}"}, {"{last_name}"}, {"{rep_name}"}, and{" "}
                {"{property_address}"} are filled in for each lead.
              </p>
            </li>
          ))}
        </ol>
        <Button type="button" variant="outline" onClick={addStep}>
          <Plus />
          Add email step
        </Button>
      </section>
    </div>
  );
};

SequencesPage.path = "/sequences";
